import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import InfoTooltipWrapper from '../../components/dashboard/InfoTooltipWrapper';
import { apiClient } from '../../api/client';
import { Mail, Send, AlertTriangle, CheckCircle2, Loader2, Users, Download } from 'lucide-react';

const SEGMENT_META = [
  { code: 'TEEN',        label: 'Teen (13-17)',          color: '#7B4FD8' },
  { code: 'PARENT',      label: 'Parent',                color: '#2563EB' },
  { code: 'ADULT',       label: 'Adult learner',         color: '#F5831F' },
  { code: 'SCHOOL',      label: 'School / Education',    color: '#2EAA5C' },
  { code: 'CORPORATE',   label: 'Workplace / Corporate', color: '#DC2626' },
  { code: 'UNSPECIFIED', label: 'Unspecified',           color: '#7A6A5A' },
  { code: 'ALL',         label: 'All segments',          color: '#1A1209' },
];

const AdminWaitlistBroadcast = () => {
  const [counts, setCounts] = useState({ total: 0, by_segment: {} });
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const [segment, setSegment] = useState('PARENT');
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [testOnly, setTestOnly] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Feb 2026 — signup list panel. Robert wanted the actual names/emails
  // collected from /pre-launch visible in the CC so he can follow up
  // manually or export for the newsletter. Test-account rows are hidden
  // by default (same classifier the Users page uses).
  const [signups, setSignups] = useState([]);
  const [signupsLoading, setSignupsLoading] = useState(false);
  const [signupsSegment, setSignupsSegment] = useState('ALL');

  const loadSignups = useCallback(async (seg) => {
    setSignupsLoading(true);
    try {
      const q = seg && seg !== 'ALL' ? `?segment=${seg}` : '';
      const data = await apiClient.request(`/api/admin/waitlist/rows${q}`);
      setSignups(data?.rows || []);
    } catch (e) { /* silent */ } finally {
      setSignupsLoading(false);
    }
  }, []);

  // Feb 2026 — one-click test-all: fires 5 branded previews to
  // TEST_RECIPIENT, one for each canonical segment template. Result
  // summary tells Robert which segments landed and which errored.
  const [testAllRunning, setTestAllRunning] = useState(false);
  const handleTestAllSegments = async () => {
    setTestAllRunning(true);
    setResult(null);
    try {
      const data = await apiClient.request('/api/admin/waitlist/test-all-segments', { method: 'POST' });
      setResult({ kind: 'success', payload: data, mode: 'test-all' });
      await loadBroadcastLog();  // refresh Recent Broadcasts inline
    } catch (e) {
      setResult({ kind: 'error', message: e?.message || 'Test-all failed' });
    } finally {
      setTestAllRunning(false);
    }
  };

  // Feb 2026 — Recent broadcasts (last 20 audit-log rows) shown inline
  // at the bottom so Robert can verify exactly what has been sent
  // without leaving the page.
  const [broadcastLog, setBroadcastLog] = useState([]);
  const [broadcastLogLoading, setBroadcastLogLoading] = useState(false);
  const loadBroadcastLog = useCallback(async () => {
    setBroadcastLogLoading(true);
    try {
      const data = await apiClient.request('/api/admin/waitlist/broadcast-log?limit=20');
      setBroadcastLog(data?.rows || []);
    } catch (e) { /* silent */ } finally {
      setBroadcastLogLoading(false);
    }
  }, []);

  const loadCounts = useCallback(async () => {
    try {
      const data = await apiClient.request('/api/admin/waitlist/segments');
      setCounts(data || { total: 0, by_segment: {} });
    } catch (e) { /* silent */ }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const data = await apiClient.request('/api/admin/waitlist/templates');
      setTemplates(data?.segments || []);
    } catch (e) { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCounts(); loadTemplates(); loadSignups('ALL'); loadBroadcastLog(); }, [loadCounts, loadTemplates, loadSignups, loadBroadcastLog]);

  // Pre-fill from canonical template when segment changes
  const loadCanonicalCopy = () => {
    const t = templates.find(t => t.code === segment);
    if (t) {
      setSubject(t.subject);
      setHtmlBody(t.html_body);
    }
  };

  const recipientCount = segment === 'ALL'
    ? counts.total
    : (counts.by_segment?.[segment] || 0);

  const handleSend = async () => {
    if (!subject.trim() || !htmlBody.trim()) {
      setResult({ kind: 'error', message: 'Subject and email body are required.' });
      return;
    }
    if (!testOnly) {
      const confirm = window.confirm(
        `You are about to send a LIVE email to ${recipientCount} recipient(s) in segment ${segment}. Continue?`
      );
      if (!confirm) return;
    }
    setSending(true);    setResult(null);
    try {
      const data = await apiClient.request('/api/admin/waitlist/broadcast', {
        method: 'POST',
        body: JSON.stringify({ segment, subject, html_body: htmlBody, test_only: testOnly }),
      });
      setResult({ kind: 'success', payload: data });
      await loadBroadcastLog();
    } catch (e) {
      setResult({ kind: 'error', message: e?.message || 'Send failed' });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center p-12">
          <Loader2 className="animate-spin" size={32} style={{ color: '#F5831F' }} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Feb 2026 — was `max-w-4xl mx-auto` which left half the CC
          content column empty on wide screens. `max-w-none` lets the
          page fill the available width like every other CC page. */}
      <div className="max-w-none p-6" data-testid="admin-waitlist-broadcast">
        <div className="flex items-center gap-3 mb-2">
          <Mail size={26} style={{ color: '#F5831F' }} />
          <h1 className="text-2xl font-bold" style={{ color: '#1A1209' }}>Waitlist Broadcast</h1>
        </div>
        <p className="text-sm text-gray-600 mb-6">
          Send a personalised email to everyone on the waitlist matching a segment.
          Use <code className="bg-gray-100 px-1 rounded text-xs">{`{{first_name}}`}</code> and{' '}
          <code className="bg-gray-100 px-1 rounded text-xs">{`{{interest_label}}`}</code> as merge fields.
        </p>

        {/* Signups list — the actual names + emails collected from
            /pre-launch. Feb 2026: added so Robert can eyeball leads,
            spot-check follow-ups, and export a CSV for the newsletter. */}
        <InfoTooltipWrapper
          testId="waitlist-signups"
          marginBottom="mb-4"
          title="Signups from /pre-launch"
          description={
            'Every real signup captured on hibff.com/pre-launch (name · email · interest · location · source · when).\n\n' +
            '• Test / seed rows are auto-hidden — same classifier as the Users page.\n' +
            '• Filter by segment (Teen · Parent · Adult · School · Corporate · Unspecified · All).\n' +
            '• Click any email to open your mail client for a personal follow-up.\n' +
            '• Export CSV downloads the current filtered view — drop it straight into a newsletter tool.'
          }
        >
          <section className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="waitlist-signups-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Users size={18} style={{ color: '#F5831F' }} />
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">Signups</h2>
              <span className="text-xs text-gray-500" data-testid="signups-count">
                ({signups.length} shown{signupsSegment !== 'ALL' ? ` · ${signupsSegment}` : ''})
              </span>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={signupsSegment}
                onChange={(e) => { setSignupsSegment(e.target.value); loadSignups(e.target.value); }}
                className="text-xs border border-gray-300 rounded px-2 py-1"
                data-testid="signups-segment-filter"
              >
                {SEGMENT_META.map(s => (
                  <option key={s.code} value={s.code}>{s.label}</option>
                ))}
              </select>
              <button
                onClick={() => {
                  const rows = [
                    ['first_name', 'email', 'interest', 'town', 'state', 'source', 'utm_campaign', 'created_at'],
                    ...signups.map(s => [
                      s.first_name || '', s.email || '', s.interest_label || s.interest_code || '',
                      s.town || '', s.state || '', s.source || '', s.utm_campaign || '',
                      s.created_at || '',
                    ]),
                  ];
                  const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(blob);
                  a.download = `hibff-waitlist-${signupsSegment.toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`;
                  a.click();
                }}
                disabled={signups.length === 0}
                className="inline-flex items-center gap-1 text-xs font-semibold text-purple-700 hover:bg-purple-50 rounded px-2 py-1 disabled:opacity-50"
                data-testid="signups-export-csv"
              >
                <Download size={12} /> Export CSV
              </button>
            </div>
          </div>
          {signupsLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="animate-spin text-gray-400" size={18} />
            </div>
          ) : signups.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">No signups yet in this segment.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="signups-table">
                <thead className="bg-gray-50 text-xs text-gray-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Name</th>
                    <th className="text-left px-3 py-2 font-semibold">Email</th>
                    <th className="text-left px-3 py-2 font-semibold">Interest</th>
                    <th className="text-left px-3 py-2 font-semibold">Location</th>
                    <th className="text-left px-3 py-2 font-semibold">Source</th>
                    <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {signups.map(s => (
                    <tr key={s.waitlist_id} data-testid={`signup-row-${s.waitlist_id}`}>
                      <td className="px-3 py-2 font-medium text-gray-800">{s.first_name || '—'}</td>
                      <td className="px-3 py-2">
                        <a href={`mailto:${s.email}`} className="text-purple-700 hover:underline">{s.email}</a>
                      </td>
                      <td className="px-3 py-2 text-gray-700">{s.interest_label || s.interest_code || '—'}</td>
                      <td className="px-3 py-2 text-gray-600 text-xs">
                        {[s.town, s.state].filter(Boolean).join(', ') || '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-500 text-xs">
                        {s.utm_campaign ? `${s.source} · ${s.utm_campaign}` : (s.source || '—')}
                      </td>
                      <td className="px-3 py-2 text-gray-500 text-xs whitespace-nowrap">
                        {s.created_at ? new Date(s.created_at).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        </InfoTooltipWrapper>

        {/* Segment picker */}
        <InfoTooltipWrapper
          testId="broadcast-segment"
          marginBottom="mb-4"
          title="1 · Choose Segment"
          description={
            'Pick who the broadcast goes to. Each tile shows a live count of active signups in that segment.\n\n' +
            '• Teen / Parent / Adult learner / School / Workplace / Corporate — filtered by the interest they selected on /pre-launch.\n' +
            '• Unspecified = they signed up without picking an interest.\n' +
            '• All segments = every active row (use sparingly, high-blast-radius).\n' +
            '• The badge on the right shows the total active waitlist across every segment.'
          }
        >
        <section className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="broadcast-segment-picker">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">1. Choose segment</h2>
            <span className="text-xs text-gray-500" data-testid="broadcast-total">
              Total active waitlist: <strong>{counts.total}</strong>
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {SEGMENT_META.map(s => {
              const n = s.code === 'ALL' ? counts.total : (counts.by_segment?.[s.code] || 0);
              const active = segment === s.code;
              return (
                <button
                  key={s.code}
                  onClick={() => setSegment(s.code)}
                  className="rounded-lg p-3 text-left transition border-2"
                  style={{
                    borderColor: active ? s.color : '#E5E7EB',
                    backgroundColor: active ? `${s.color}15` : 'white',
                  }}
                  data-testid={`segment-${s.code.toLowerCase()}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded text-white"
                      style={{ backgroundColor: s.color }}
                    >{s.code}</span>
                  </div>
                  <p className="text-xs text-gray-600 leading-tight">{s.label}</p>
                  <p className="text-lg font-bold tabular-nums mt-1" style={{ color: s.color }}>{n}</p>
                </button>
              );
            })}
          </div>
        </section>
        </InfoTooltipWrapper>

        {/* Compose */}
        <InfoTooltipWrapper
          testId="broadcast-compose"
          marginBottom="mb-4"
          title="2 · Compose"
          description={
            'Write the actual message. Two merge fields are auto-substituted at send time:\n\n' +
            '• {{first_name}} → the recipient\'s first name (falls back to "there" if blank).\n' +
            '• {{interest_label}} → their chosen segment ("Parent", "Adult learner", "School" etc).\n\n' +
            'The "Load canonical template" button drops in a founder-approved starter for the currently-selected segment so you don\'t start from scratch.\n\n' +
            'HTML body is the message *inside* the branded shell — the HiBFF logo header (clickable, links to hibff.com) and the footer signature are auto-wrapped around your body at send time. Do not include a header/footer yourself.'
          }
        >
        <section className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="broadcast-compose">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">2. Compose</h2>
            <button
              onClick={loadCanonicalCopy}
              className="text-xs font-semibold text-purple-700 hover:underline"
              data-testid="broadcast-load-template"
            >
              ↻ Load canonical {segment} template
            </button>
          </div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder="Hi {{first_name}} — your HiBFF invite is here"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
            data-testid="broadcast-subject"
          />
          <label className="block text-xs font-medium text-gray-600 mb-1">HTML body</label>
          <textarea
            value={htmlBody}
            onChange={e => setHtmlBody(e.target.value)}
            rows={10}
            placeholder="<p>Hi {{first_name}},</p><p>We're ready to welcome you...</p>"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
            data-testid="broadcast-body"
          />
          <p className="text-xs text-gray-500 mt-2">
            The HiBFF branded header (logo → hibff.com) and footer are automatically wrapped around this body at send time — no need to include them here.
          </p>

          {/* Feb 2026 — one-click preview for every product/segment.
              Fires 5 branded test emails (Teen, Parent, Adult, School,
              Corporate) to robert@rparson.co.uk in one shot so every
              audience's canonical copy can be eyeballed side-by-side. */}
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
            <div className="text-xs text-gray-600 max-w-md">
              <span className="font-semibold text-gray-800">Test everything at once —</span>{' '}
              sends the canonical email for every product/segment to <code className="bg-gray-100 px-1 rounded">robert@rparson.co.uk</code>, so you can see how each audience receives it before going live.
            </div>
            <button
              type="button"
              onClick={handleTestAllSegments}
              disabled={testAllRunning}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg text-white shadow-sm disabled:opacity-60"
              style={{ background: '#7B4FD8' }}
              data-testid="broadcast-test-all-segments"
            >
              {testAllRunning
                ? <><Loader2 size={14} className="animate-spin" /> Sending 5 previews…</>
                : <><Send size={14} /> Send test to every segment</>
              }
            </button>
          </div>
        </section>
        </InfoTooltipWrapper>

        {/* Send */}
        <InfoTooltipWrapper
          testId="broadcast-send"
          marginBottom="mb-4"
          title="3 · Send"
          description={
            'Fire the broadcast — or a test preview first.\n\n' +
            '• Test only (recommended, default ON) — Postmark sends ONE merged preview to robert@rparson.co.uk using the first matching row, so you can eyeball the rendered HTML before hitting a real recipient.\n' +
            '• Uncheck Test only → you\'ll be asked to confirm again, then all recipients in the segment get the email.\n\n' +
            'Every send (test or live) is logged to `audit_logs` with segment, count, subject, and admin email so you have a clean audit trail.'
          }
        >
        <section className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="broadcast-send">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700 mb-3">3. Send</h2>
          <label className="flex items-start gap-3 mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={testOnly}
              onChange={e => setTestOnly(e.target.checked)}
              className="mt-1"
              data-testid="broadcast-test-only"
            />
            <div className="text-sm">
              <p className="font-semibold text-gray-900">Test only (recommended)</p>
              <p className="text-xs text-gray-600">
                Sends one merged preview to <code>robert@rparson.co.uk</code> using the first matching row,
                so you can QA the rendered email before unleashing it on {recipientCount} real recipients.
              </p>
            </div>
          </label>

          {!testOnly && (
            <div
              className="rounded-lg p-3 mb-4 flex items-start gap-2"
              style={{ backgroundColor: '#FEF3C7', border: '1px solid #F59E0B' }}
              data-testid="broadcast-live-warning"
            >
              <AlertTriangle size={18} style={{ color: '#B45309' }} className="shrink-0 mt-0.5" />
              <div className="text-xs" style={{ color: '#7C2D12' }}>
                <strong>LIVE SEND.</strong> This will email <strong>{recipientCount}</strong> real recipient(s)
                in the <strong>{segment}</strong> segment. There is no undo. Make sure you have previewed in test mode first.
              </div>
            </div>
          )}

          <button
            onClick={handleSend}
            disabled={sending || !subject.trim() || !htmlBody.trim() || (recipientCount === 0 && !testOnly)}
            className="w-full sm:w-auto px-6 py-3 rounded-lg font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50 transition"
            style={{ backgroundColor: testOnly ? '#7B4FD8' : '#DC2626' }}
            data-testid="broadcast-send-button"
          >
            {sending ? (
              <><Loader2 className="animate-spin" size={18} /> Sending…</>
            ) : (
              <><Send size={18} /> {testOnly ? `Send test preview` : `Send to ${recipientCount} recipient${recipientCount === 1 ? '' : 's'}`}</>
            )}
          </button>
        </section>
        </InfoTooltipWrapper>

        {/* Result */}
        {result && result.kind === 'success' && result.mode === 'test-all' && (
          <section
            className="rounded-2xl p-5 mb-4 flex items-start gap-3"
            style={{ backgroundColor: '#F5F3FF', border: '1px solid #7B4FD8' }}
            data-testid="broadcast-result-test-all"
          >
            <CheckCircle2 size={22} style={{ color: '#7B4FD8' }} className="shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold mb-1" style={{ color: '#4C1D95' }}>
                Test-all dispatched · <strong>{result.payload.sent}</strong>/{result.payload.sent + result.payload.failed} branded previews landed
              </p>
              <p className="text-xs leading-relaxed mb-2" style={{ color: '#4C1D95' }}>
                Test recipient: <code>{result.payload.test_recipient}</code>. Check your inbox for one email per segment (subject prefixed with <code>[TEST · SEGMENT]</code>).
              </p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {(result.payload.results || []).map((r) => (
                  <span
                    key={r.segment}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                    style={{
                      background: r.ok ? '#D1FAE5' : '#FEE2E2',
                      color: r.ok ? '#065F46' : '#7F1D1D',
                    }}
                    title={r.ok ? 'Delivered to Postmark' : (r.error || 'send failed')}
                  >
                    {r.ok ? '✓' : '✗'} {r.segment}
                  </span>
                ))}
              </div>
            </div>
          </section>
        )}
        {result && result.kind === 'success' && result.mode !== 'test-all' && (
          <section
            className="rounded-2xl p-5 mb-4 flex items-start gap-3"
            style={{ backgroundColor: '#ECFDF5', border: '1px solid #10B981' }}
            data-testid="broadcast-result-success"
          >
            <CheckCircle2 size={22} style={{ color: '#059669' }} className="shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold mb-1" style={{ color: '#065F46' }}>
                {result.payload.test_only ? 'Test preview dispatched' : 'Broadcast complete'}
              </p>
              <p className="text-xs leading-relaxed" style={{ color: '#065F46' }}>
                Segment <strong>{result.payload.segment}</strong> · Matched <strong>{result.payload.matched}</strong> ·
                Sent <strong>{result.payload.sent}</strong> · Failed <strong>{result.payload.failed}</strong>
                {result.payload.test_recipient && (
                  <> · Test recipient: <code>{result.payload.test_recipient}</code></>
                )}
              </p>
            </div>
          </section>
        )}
        {result && result.kind === 'error' && (
          <section
            className="rounded-2xl p-5 mb-4 flex items-start gap-3"
            style={{ backgroundColor: '#FEF2F2', border: '1px solid #DC2626' }}
            data-testid="broadcast-result-error"
          >
            <AlertTriangle size={22} style={{ color: '#B91C1C' }} className="shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold mb-1" style={{ color: '#7F1D1D' }}>Broadcast failed</p>
              <p className="text-xs" style={{ color: '#7F1D1D' }}>{result.message}</p>
            </div>
          </section>
        )}

        <p className="text-xs text-gray-500 mt-6 flex items-center gap-2">
          <Users size={12} /> All sends are logged in <code>audit_logs</code> with the segment, counts, and admin email.
        </p>

        {/* Recent broadcasts — inline audit history so Robert can eyeball
            what has (and hasn't) been sent without leaving the page. */}
        <InfoTooltipWrapper
          testId="broadcast-recent"
          marginBottom="mt-6"
          title="Recent broadcasts"
          description={
            'Live tail of your last 20 waitlist sends, pulled straight from `audit_logs`.\n\n' +
            '• Each row shows when · who sent it · segment · mode (test / live) · recipient count · subject.\n' +
            '• `waitlist_broadcast` = a single-segment send (one row per broadcast).\n' +
            '• `waitlist_broadcast_test_all` = the "test every segment" batch (one row for the whole batch of 5 previews).\n' +
            '• Refresh button re-pulls the log. Anything you send from this page appears here within seconds.'
          }
        >
        <section className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="broadcast-recent-panel">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">Recent broadcasts (last 20)</h2>
            <button
              type="button"
              onClick={loadBroadcastLog}
              disabled={broadcastLogLoading}
              className="text-xs font-semibold text-purple-700 hover:bg-purple-50 rounded px-2 py-1 disabled:opacity-50"
              data-testid="broadcast-log-refresh"
            >
              {broadcastLogLoading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
          {broadcastLog.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">No broadcasts logged yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="broadcast-log-table">
                <thead className="bg-gray-50 text-xs text-gray-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">When</th>
                    <th className="text-left px-3 py-2 font-semibold">Segment</th>
                    <th className="text-left px-3 py-2 font-semibold">Mode</th>
                    <th className="text-left px-3 py-2 font-semibold">Sent</th>
                    <th className="text-left px-3 py-2 font-semibold">Subject / Batch</th>
                    <th className="text-left px-3 py-2 font-semibold">Admin</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {broadcastLog.map((row) => {
                    const m = row.metadata || {};
                    const isBatch = row.action === 'waitlist_broadcast_test_all';
                    const segLabel = isBatch ? `Batch · ${(m.segments || []).length}` : (m.segment || '—');
                    const mode = isBatch ? 'test-all' : (m.test_only ? 'test' : 'live');
                    const modeColor = mode === 'live' ? '#DC2626' : (mode === 'test-all' ? '#7B4FD8' : '#059669');
                    return (
                      <tr key={row.log_id} data-testid={`broadcast-log-row-${row.log_id}`}>
                        <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                          {row.timestamp ? new Date(row.timestamp).toLocaleString() : '—'}
                        </td>
                        <td className="px-3 py-2 text-gray-800 font-medium">{segLabel}</td>
                        <td className="px-3 py-2">
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
                            style={{ background: modeColor + '18', color: modeColor }}
                          >
                            {mode}
                          </span>
                        </td>
                        <td className="px-3 py-2 tabular-nums text-gray-700">
                          {m.sent ?? 0}{m.failed ? <span className="text-red-600"> · {m.failed} failed</span> : ''}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-700 max-w-md truncate">
                          {isBatch ? `${(m.segments || []).join(' · ')}` : (m.subject || '—')}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500 truncate">{m.admin_email || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
        </InfoTooltipWrapper>
      </div>
    </AdminLayout>
  );
};

export default AdminWaitlistBroadcast;
